import { useEffect, useRef } from 'react';
import api from '../../../services/api';

/**
 * Share journey tracking: engagement events from the public gallery pages
 * (page-view, media-view, download, reshare, cta-click), attributed to a
 * share destination via the personal gallery slug in the URL. Events go to
 * the public POST /journey/log endpoint — same trust model as /stat/log-stat.
 *
 * The slug comes from the route (/gallery/:slug/user/:destinationSlug,
 * /photos/:slug) or from the ?u= query param that share links carry on the
 * link shapes whose paths don't include it (/session/…, /photo/…).
 *
 * Page-level components declare the journey context once with
 * useJourneyPageView (which also logs the page-view); shared components
 * (lightbox, download button, CTA, share modal) just call logJourneyEvent.
 */

export type JourneyEventType =
    | 'page-view'
    | 'media-view'
    | 'download'
    | 'download-all'
    | 'reshare'
    | 'cta-click';

export interface JourneyContext {
    activationId?: number | null;
    galleryId?: number | null;
    slug?: string | null;
}

const VISIT_STORAGE_KEY = 'sb-journey-visit';

let journeyContext: JourneyContext = {};

function getSlugFromQuery(): string | null {
    try {
        return new URLSearchParams(window.location.search).get('u');
    } catch {
        return null;
    }
}

function getVisitId(): string | null {
    try {
        let visitId = sessionStorage.getItem(VISIT_STORAGE_KEY);

        if (!visitId) {
            visitId = `${Date.now().toString(36)}-${Math.random()
                .toString(36)
                .slice(2, 12)}`;
            sessionStorage.setItem(VISIT_STORAGE_KEY, visitId);
        }

        return visitId;
    } catch {
        return null;
    }
}

export function setJourneyContext(context: JourneyContext) {
    journeyContext = {
        activationId: context.activationId ?? null,
        galleryId: context.galleryId ?? null,
        slug: context.slug ?? getSlugFromQuery(),
    };
}

export function logJourneyEvent(
    type: JourneyEventType,
    extra: {
        mediaId?: number;
        metadata?: Record<string, unknown>;
    } = {},
) {
    try {
        const event = {
            type,
            activationId: journeyContext.activationId ?? undefined,
            galleryId: journeyContext.galleryId ?? undefined,
            slug: journeyContext.slug ?? getSlugFromQuery() ?? undefined,
            visitId: getVisitId() ?? undefined,
            mediaId: extra.mediaId,
            metadata: extra.metadata,
        };

        // Fire-and-forget: tracking must never affect the guest experience.
        api.post('/journey/log', { events: [event] }).catch(() => {});
    } catch {}
}

/**
 * Declares the journey context for the current page and logs a single
 * page-view once `ready` is true (i.e. once the gallery has loaded enough to
 * know its ids). A gallery that immediately forwards elsewhere still counts —
 * the link was opened.
 */
export function useJourneyPageView(context: JourneyContext, ready: boolean) {
    const viewLogged = useRef(false);

    useEffect(() => {
        if (!ready) {
            return;
        }

        setJourneyContext(context);

        if (!viewLogged.current) {
            viewLogged.current = true;
            logJourneyEvent('page-view');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, context.activationId, context.galleryId, context.slug]);
}
